import { useState, useEffect, useRef, useCallback } from "react";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined"; // Import the logout icon
import { Slide } from "@mui/material";
import ProfileFooter_Tasks from "./profileFooter_Tasks";
import PropTypes from "prop-types";
import supabase from "../../api/supabase"; // Ensure correct path to your Supabase instance
import { useNavigate } from "react-router-dom"; // Import useNavigate for redirection
import useCurrentUser from "../user/useCurrentUser"; // Import the hook to manage user data

const ProfileFooter = ({ empUserId }) => {
  const [activeTab, setActiveTab] = useState(null); // Track the currently active tab
  const [isBoxOpen, setIsBoxOpen] = useState(false); // Toggle box visibility
  const footerRef = useRef(null); // Ref for the BottomNavigation component
  const slidingBoxRef = useRef(null); // Ref for the sliding box
  const navigate = useNavigate(); // Initialize navigate for redirecting
  const { clearUserProfile } = useCurrentUser(); // Get the function to clear user data

  const handleLogout = async () => {
    try {
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !data.session) {
        console.log("No active session found. Already logged out.");
        clearUserProfile();
        navigate("/login");
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error logging out:", error.message);
        return;
      }

      console.log("Logged out successfully");
      clearUserProfile();
      navigate("/login");
    } catch (err) {
      console.error("Unexpected error during logout:", err);
    }
  };

  const handleToggleBox = (tab) => {
    // If the Log out button (tab 3) is clicked, don't toggle the slider, log out instead
    if (tab === 3) {
      setActiveTab(tab);
      handleLogout(); // Trigger log-out without opening the slide
      return;
    }

    // If the same tab is clicked, only toggle the slider visibility
    if (activeTab === tab) {
      setIsBoxOpen(!isBoxOpen); // Toggle visibility if the same tab is clicked
    } else {
      setActiveTab(tab); // Update active tab when a new tab is clicked
      setIsBoxOpen(true); // Open the box with the new content
    }
  };

  const handleClickOutside = useCallback((event) => {
    if (
      isBoxOpen &&
      slidingBoxRef.current &&
      !slidingBoxRef.current.contains(event.target) &&
      !footerRef.current.contains(event.target)
    ) {
      setIsBoxOpen(false); // Close the box if clicked outside
    }
  }, [isBoxOpen]); // Add isBoxOpen as a dependency since it's used inside

  useEffect(() => {
    if (isBoxOpen) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isBoxOpen, handleClickOutside]);

  const renderContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <>
            <p>Home Content</p>
            <p>Content coming soon...</p>
          </>
        );
      case 1:
        return <ProfileFooter_Tasks empUserId={empUserId} />;
      case 2:
        return (
          <>
            <p>Settings Content</p>
            <p>Content coming soon...</p>
          </>
        );
      default:
        return "Home Content";
    }
  };

  return (
    <div
      ref={footerRef}
      className="footer-wrapper mx-auto"
      style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 10 }}
    >
      {/* Bottom Navigation */}
      <BottomNavigation
        value={activeTab}
        onChange={(event, newValue) => {
          handleToggleBox(newValue); // Handle toggle when a tab is clicked
        }}
        showLabels
        sx={{
          marginLeft: "10px",
          marginRight: "10px",
          borderTop: "1px solid #e5e5e5",
          borderLeft: "1px solid #e5e5e5",
          borderRight: "1px solid #e5e5e5",
          p: 1,
          backgroundColor: "#f5f5f5", // Ensure the background is visible
        }}
      >
        <BottomNavigationAction
          label="Home"
          icon={<HomeOutlinedIcon />}
          sx={{
            "&.Mui-selected": {
              color: "black", // Change active icon and label color to black
            },
          }}
        />
        <BottomNavigationAction
          label="Tasks"
          icon={<AssignmentOutlinedIcon />}
          sx={{
            "&.Mui-selected": {
              color: "black", // Change active icon and label color to black
            },
          }}
        />
        <BottomNavigationAction
          label="Settings"
          icon={<SettingsOutlinedIcon />}
          sx={{
            "&.Mui-selected": {
              color: "black", // Change active icon and label color to black
            },
          }}
        />
        <BottomNavigationAction
          label="Log out"
          icon={<LogoutOutlinedIcon />} // Use a logout icon
          sx={{
            "&.Mui-selected": {
              color: "black", // Change active icon and label color to black
            },
          }}
        />
      </BottomNavigation>

      {/* Sliding Box */}
      <Slide direction="up" in={isBoxOpen} mountOnEnter unmountOnExit>
        <div
          ref={slidingBoxRef}
          className="sliding-box max-w-md mx-auto"
          style={{
            position: "fixed", // Keep it fixed along with the footer
            bottom: "55px", // Adjust to stay above footer
            left: "10px",
            right: "10px",
            height: "400px", // Customize height as needed
            backgroundColor: "#f5f5f5",
            border: "1px solid #e5e5e5",
            borderBottom: "none",
            borderTopLeftRadius: "30px", // Rounded top-left corner
            borderTopRightRadius: "30px", // Rounded top-right corner
            padding: "20px",
            zIndex: 50,
            overflowY: "auto", // Enable vertical scrolling
          }}
          onClick={(e) => e.stopPropagation()} // Prevent clicks inside the box from closing it
        >
          {/* Dynamic Content */}
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            {renderContent()} {/* Render content based on active tab */}
          </div>
        </div>
      </Slide>
    </div>
  );
};

// Add prop validation using PropTypes
ProfileFooter.propTypes = {
  empUserId: PropTypes.string.isRequired, // empUserId is required and must be a string
};

export default ProfileFooter;
